import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function MenuCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      menu_name: "",
      route: "",
      icon: "",
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      menu_name: Yup.string().required("Menu Name is required"),
      route: Yup.string().required("Route is required"),
      sort_order: Yup.string().required("Sort Order is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        menu_name: values.menu_name,
        route: values.route,
        icon: values.icon,
        sort_order: values.sort_order,
        status: values.isActive === "Active",
      };

      try {
        let response;
        if (isEdit) {
          response = await request.put("menus/" + id, payload);
        } else {
          response = await request.post("menus/create", payload);
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "Menu updated successfully!" : "Menu created successfully!");
          navigate("/admin/menus");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`menus/${id}`);
          const item = response.data.data;
          formik.setValues({
            menu_name: item.menu_name || "",
            route: item.route || "",
            icon: item.icon || "",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Menu" : "Create New Menu"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/menus"),
              }}
              primaryButton={{
                label: isEdit ? "Update Menu" : "Create Menu",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="menu_name" label="Menu Name" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="route" label="Route" type="text" formik={formik} placeholder="/results" />
                </Box>
              </Box>

              <Box className="form-grid form-grid--3" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="icon" label="Icon" type="text" formik={formik} placeholder="e.g. Scoreboard" />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}